// UI copy is localized via the account:manage_members.* / account:roles.* catalogs (all 6 locales).
import React, { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import Button from "components/Button";
import Input from "components/Input";
import Modal from "components/Modal";
import DeleteModal from "components/DeleteModal";
import Tooltip from "components/Tooltip";
import ProgressBar from "components/ProgressBar";
import ToggleButton from "components/ToggleButton";
import { IconChevron, IconHelp, IconMore, IconPlus, IconSearch, IconUser, IconWarning } from "icons";
import teamsService from "api/teams/teams.service";
import paymentService from "api/payment/payment.service";
import userService from "api/user/user.service";
import { ITeamCreditUsageMember, ITeamCreditUsageResponse, ITeamRosterMember, ITeamRosterPendingInvite, ITeamRosterResponse, TeamInviteRole, TeamRole } from "api/teams/teams.type";
import { useAppSelector, useAppDispatch } from "store";
import { useSnackbar } from "hooks/useSnackbar";
import SeatStepper from "components/SeatStepper";
import usePaymentActionHandler, { isRemoveMembersFirst } from "hooks/usePaymentActionHandler";
import ManageMemberModal from "./ManageMemberModal";
import { AnalyticsEvent, formatDate, DateFormat, humanizePackage } from "utils";
import { getReasonCode, reasonErrorText } from "utils/reasonError";
import { selectPrimaryPlan } from "utils/planStatus";
import { setAccountSelectedMenu } from "store/commonSlice";
import { AccountSelectMenu } from "pages/Account/Account";
import i18n from "i18n";

type RemoveTarget = { kind: "member"; member: ITeamRosterMember } | { kind: "invite"; invite: ITeamRosterPendingInvite } | null;

const ROLES: TeamInviteRole[] = ["Member", "Admin"];

// Product only exposes two role semantics — Admin and Standard User. Legacy "Viewer"/"Editor"
// values surfaced by the roster are non-admin and collapse to the same label. Returns the i18n
// key so call sites resolve it with t().
const roleLabelKey = (role: TeamRole): string => (role === "Admin" ? "account:roles.admin" : "account:roles.standard_user");

const adminFirst = <T extends { role: TeamRole }>(arr: T[]): T[] => [...arr].sort((a, b) => (a.role === "Admin" ? -1 : 0) - (b.role === "Admin" ? -1 : 0));

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Legacy invites pre-dating the expiry migration return expires_at as null. Also guard against
// malformed strings — `new Date(null)` silently returns the Unix epoch, so try/catch alone wasn't enough.
const formatExpiresAt = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;

  return d.toLocaleString(i18n.language || "en", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// REF-5549: null/invalid-safe SHORT date for the credit-validity line. Delegates to the shared
// locale-aware formatter (uses i18n.language) so the team date matches the rest of the app and
// picks up the pt year-token fix — instead of a hand-rolled browser-locale toLocaleDateString.
const formatPlanDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const out = formatDate(iso, DateFormat.SHORT);

  return out === "Invalid Date" ? null : out;
};

// Info-icon tooltip explaining a credit number. `tip` is already translated by the caller.
const InfoTip = ({ tip }: { tip: string }) => (
  <Tooltip content={tip} position="top" contentClassName="!whitespace-normal max-w-[200px] leading-snug">
    <IconHelp className="w-3.5 h-3.5 text-grey cursor-help" />
  </Tooltip>
);

// One cell in the top stat dashboard. Cells sit on a gap-px black-300 grid so the gaps read as gridlines.
const StatCard = ({ label, children }: { label: React.ReactNode; children: React.ReactNode }) => (
  <div className="flex flex-col gap-1.5 p-4 bg-body-100">
    <span className="flex items-center gap-1 text-bodySm text-grey">{label}</span>
    {children}
  </div>
);

const Avatar = ({ name, profilePhoto }: { name: string; profilePhoto: string | null }) => (
  <div className="flex-center rounded-full bg-primary w-9 h-9 text-h7 text-primary-foreground uppercase overflow-hidden flex-shrink-0">
    {profilePhoto ? <img src={profilePhoto} alt={name} className="w-full h-full object-cover" /> : name?.[0] ?? <IconUser className="w-4 h-4" />}
  </div>
);

const RoleBadge = ({ role }: { role: TeamRole }) => {
  const { t } = useTranslation();

  return (
    <span className={clsx("text-bodySm px-2 py-0.5 rounded whitespace-nowrap", role === "Admin" ? "bg-purple/20 text-purple dark:text-green dark:bg-green/20" : "bg-black-300 text-grey")}>
      {t(roleLabelKey(role))}
    </span>
  );
};

// REF-5611: "No limit" pill for unlimited members (admin, team-wide switch, or per-member flag).
const NoLimitBadge = () => {
  const { t } = useTranslation();

  return <span className="text-bodySm px-2 py-0.5 rounded whitespace-nowrap w-fit bg-purple/20 text-purple dark:text-green dark:bg-green/20">{t("account:manage_members.no_limit")}</span>;
};

type RowAction = { label: string; onClick: () => void; danger?: boolean };

// Self-contained row "⋯" overflow menu: toggles a popover, closes on outside-click / Escape.
// Renders an alignment spacer (no button) when a row has no available actions.
const RowActionsMenu = ({ actions, label }: { actions: RowAction[]; label: string }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointer = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!actions.length) return <div className="w-7 h-7" />;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex-center w-7 h-7 rounded-md text-grey hover:text-trueBlack dark:hover:text-white hover:bg-black-300/60"
        onClick={() => setOpen((o) => !o)}
      >
        <IconMore className="w-4 h-4" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-full mt-1 z-20 min-w-[170px] flex flex-col p-1 rounded-md border border-black-300 bg-body-100 shadow-lg">
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              role="menuitem"
              className={clsx("text-left text-bodySm px-2.5 py-1.5 rounded hover:bg-black-300/60", action.danger ? "text-orange" : "text-trueBlack dark:text-white")}
              onClick={() => {
                setOpen(false);
                action.onClick();
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface SeatChangeModalProps {
  onClose: () => void;
  currentSeats: number;
  seatUsed: number;
  // Pre-set the stepper (e.g. the invite seat-upsell opens it at `suggested_seats`).
  initialTarget?: number;
  // Optional context line shown above the stepper (e.g. "You're out of seats. Add a seat to invite …").
  intro?: string;
}

// REF-5549 (BE REF-5446): admin seat add/remove. POST /payments/seats returns an action list
// (HTTP 200). seat_change redirects to the Stripe portal; increases prorate immediately while
// decreases take effect at period end; a reduction below the seats actually in use is blocked
// (BE 409 with seat_used, mirrored here proactively so we don't waste a round-trip).
const SeatChangeModal: React.FC<SeatChangeModalProps> = ({ onClose, currentSeats, seatUsed, initialTarget, intro }) => {
  const { t } = useTranslation();
  const handlePaymentAction = usePaymentActionHandler();
  const [target, setTarget] = useState(initialTarget ?? currentSeats);
  const [submitting, setSubmitting] = useState(false);
  const [removeFirst, setRemoveFirst] = useState<number | null>(null);

  const isIncrease = target > currentSeats;
  const isDecrease = target < currentSeats;
  const belowUsed = target < seatUsed;
  const unchanged = target === currentSeats;

  const handleConfirm = async () => {
    setSubmitting(true);
    setRemoveFirst(null);
    AnalyticsEvent("seat_change_initiated", "", "click", { current_seats: currentSeats, target_seats: target });

    try {
      const res = await paymentService.changeSeats({ target_seats: target });
      const item = res?.responseData?.[0];

      // "Remove members before reducing seats" — inline UX, not a corner snackbar.
      // A past_due refusal is a 409 too — it goes to the handler's payment-recovery popup (PD5).
      if (item && isRemoveMembersFirst(item)) {
        setRemoveFirst(item.seat_used ?? seatUsed);

        return;
      }

      const result = handlePaymentAction(res);
      if (!result.redirected) onClose();
    } catch {
      // 401/403/etc. are handled by the interceptor; surface a generic message for anything else.
      handlePaymentAction(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} show={true} bodyClassName="!rounded-[5px] !border-none">
      <div className="flex flex-col bg-black w-[420px]">
        <div className="p-2 bg-black-300 text-h6 text-white rounded-t-[5px]">{t("account:manage_members.seat_change.title")}</div>
        <div className="flex flex-col gap-4 w-full p-4">
          {intro && <span className="text-bodySm text-white">{intro}</span>}
          <div className="flex items-center justify-between">
            <span className="text-bodySm text-grey">{t("account:manage_members.seat_change.new_count")}</span>
            <SeatStepper value={target} onChange={setTarget} min={1} />
          </div>
          <span className="text-bodySm text-grey">{t("account:manage_members.seat_change.current", { current: currentSeats, used: seatUsed })}</span>

          {isIncrease && (
            <div className="flex items-start gap-2 p-2.5 text-bodySm text-trueBlack dark:text-white bg-body-100 border border-black-300 rounded-md">
              <IconWarning className="w-4 h-4 flex-shrink-0 text-trueBlack dark:text-white mt-0.5" />
              {t("account:manage_members.seat_change.increase_warning")}
            </div>
          )}
          {isDecrease && !belowUsed && (
            <div className="flex items-start gap-2 p-2.5 text-bodySm text-trueBlack dark:text-white bg-body-100 border border-black-300 rounded-md">
              <IconWarning className="w-4 h-4 flex-shrink-0 text-trueBlack dark:text-white mt-0.5" />
              {t("account:manage_members.seat_change.decrease_warning")}
            </div>
          )}
          {(belowUsed || removeFirst !== null) && (
            <div className="flex items-start gap-2 p-2.5 text-bodySm text-orange bg-orange/10 border border-orange/30 rounded-md">
              <IconWarning className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {t("account:manage_members.seat_change.below_used_warning", { used: removeFirst ?? seatUsed, target })}
            </div>
          )}
        </div>
        <div className="flex bg-black-100 gap-2.5 p-2.5 border border-black-300 rounded-b-[5px]">
          <Button className="btn-secondary w-full" onClick={onClose} disabled={submitting}>
            {t("account:manage_members.seat_change.cancel")}
          </Button>
          <Button className="btn-primary w-full" onClick={handleConfirm} disabled={submitting || unchanged || belowUsed}>
            {submitting ? t("account:manage_members.seat_change.processing") : t("account:manage_members.seat_change.confirm")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

interface InviteModalProps {
  onInvite: (email: string, role: TeamInviteRole) => Promise<boolean>;
  disabled?: boolean;
  emailError?: string;
  clearEmailError: () => void;
  onClose: () => void;
}

// Single-email invite (no comma-separated list) in a centered modal: Email field + a standalone
// Role dropdown. Closes on a successful send; a BE error keeps it open with the inline message.
const InviteModal: React.FC<InviteModalProps> = ({ onInvite, disabled, emailError, clearEmailError, onClose }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TeamInviteRole>("Member");
  const [isSending, setIsSending] = useState(false);
  const [localError, setLocalError] = useState<string | undefined>();

  const error = emailError ?? localError;

  const handleSubmit = useCallback(async () => {
    const trimmed = email.trim();

    if (!EMAIL_RE.test(trimmed)) {
      setLocalError(t("account:manage_members.errors.invalid_email"));

      return;
    }

    setLocalError(undefined);
    clearEmailError();
    setIsSending(true);

    const ok = await onInvite(trimmed, role);

    setIsSending(false);

    if (ok) onClose();
  }, [email, role, onInvite, clearEmailError, onClose, t]);

  return (
    <Modal onClose={onClose} show={true} bodyClassName="!rounded-[5px] !border-none">
      <div className="flex flex-col bg-black w-[460px]">
        <div className="p-2 bg-black-300 text-h6 text-white rounded-t-[5px]">{t("account:manage_members.invite.title")}</div>
        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-col gap-2.5">
            <span className="text-h7 text-white">{t("account:manage_members.invite.email")}</span>
            <Input
              type="email"
              placeholder={t("account:manage_members.invite.email_placeholder")}
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setEmail(e.target.value);
                setLocalError(undefined);
                clearEmailError();
              }}
              error={error}
            />
          </div>
          <div className="flex flex-col gap-2.5">
            <span className="text-h7 text-white">{t("account:manage_members.invite.role")}</span>
            <select
              className="w-full bg-black-200 border border-black-300 text-white text-bodySm rounded-[5px] px-2.5 py-3 outline-none"
              value={role}
              onChange={(e) => setRole(e.target.value as TeamInviteRole)}
              aria-label={t("account:manage_members.invite.role")}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {t(roleLabelKey(r))}
                </option>
              ))}
            </select>
          </div>
          {disabled && (
            <div className="flex items-center gap-2 p-2.5 text-bodySm text-orange bg-orange/10 border border-orange/30 rounded-md">
              <IconWarning className="w-4 h-4 flex-shrink-0" />
              {t("account:manage_members.invite.seat_limit_warning")}
            </div>
          )}
        </div>
        <div className="flex justify-end bg-black-100 gap-2.5 p-2.5 border-t border-black-300 rounded-b-[5px]">
          <Button className="btn-secondary-medium" onClick={onClose} disabled={isSending}>
            {t("account:manage_members.invite.cancel")}
          </Button>
          <Button className="btn-primary-medium" onClick={handleSubmit} disabled={isSending || disabled}>
            {isSending ? t("account:manage_members.invite.sending") : t("account:manage_members.invite.send")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const ManageMembers = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.common);
  const { showSuccess, showError, showInfo } = useSnackbar();

  const [roster, setRoster] = useState<ITeamRosterResponse | null>(null);
  const [creditUsage, setCreditUsage] = useState<ITeamCreditUsageResponse | null>(null);
  const [manageMember, setManageMember] = useState<ITeamCreditUsageMember | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(true);
  const [removeTarget, setRemoveTarget] = useState<RemoveTarget>(null);
  // REF-5549: seat-full invite upsell. Set when teams/invite returns action: "seat_purchase_required";
  // opens the seat-change modal pre-filled at the BE-suggested seat count to drive the purchase.
  const [seatUpsell, setSeatUpsell] = useState<{ suggested: number; email: string } | null>(null);
  const [inviteEmailError, setInviteEmailError] = useState<string | undefined>();
  const [showSeatChange, setShowSeatChange] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [search, setSearch] = useState("");
  // Plan end date (e.g. "active until Mar 31, 2028") — shown as the credit validity date. Sourced
  // from the team plan's package_detail.end_date (same as PlanBilling), not a separate credit field.
  const [planEndDate, setPlanEndDate] = useState<string | null>(null);
  // Live plan name from package_detail (source of truth). team.package_type is a denormalized column
  // that can lag a package transition, so it's only a fallback here (REF-5831). Keeps this tab and
  // Plan & Billing showing the same paid plan.
  const [planName, setPlanName] = useState<string | null>(null);
  // REF-5611: team-wide no-limit switch (super-admin only). Inline error next to the switch.
  // isSuperAdmin comes from GET /users/{id}.is_super_admin (the subscription owner) — the same
  // user-detail call fetchRoster already makes for the plan date. Non-owners never see the switch.
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [policyError, setPolicyError] = useState<string | null>(null);

  // `silent` skips the page-level loading state. A non-silent refetch flips isLoading, which hits
  // the `if (isLoading)` guard below and UNMOUNTS the open member modal — on remount the modal
  // re-seeds its input from the (stale) manageMember and the just-saved value appears to "revert".
  // Post-save refreshes pass silent=true so the modal stays mounted. Returns the fresh credit usage.
  const fetchRoster = useCallback(
    async (silent = false): Promise<ITeamCreditUsageResponse | null> => {
      if (!silent) setIsLoading(true);
      let freshUsage: ITeamCreditUsageResponse | null = null;

      try {
        const res = await teamsService.getRoster();

        // 401/503 still resolve with _handled (CLAUDE.md Trap #1). 403 rejects for teams/ URLs.
        if (res?._handled) {
          setRoster(null);

          return null;
        }

        setRoster(res.responseData ?? null);
        setHasAccess(true);

        // Credit dashboard (admin-only) + plan end-date are independent of each other and of nothing
        // but the roster having loaded — fetch them concurrently so the Manage Members spinner waits
        // on max(latency) not the sum. Each degrades silently (.catch → null) so the membership UI
        // still renders without the credit columns / validity line.
        const [cuRes, detailRes] = await Promise.all([teamsService.getCreditUsage().catch(() => null), userService.getUserDetail({ user_id: user.id }).catch(() => null)]);

        // getCreditUsage: 401/503 resolve with _handled (Trap #1) — treat as "no data", not an error.
        freshUsage = cuRes && !cuRes._handled ? cuRes.responseData ?? null : null;
        setCreditUsage(freshUsage);

        // Plan end date — same source as PlanBilling (the team plan's package_detail.end_date).
        const detailData = detailRes && !detailRes._handled ? detailRes.responseData : null;
        const pkgs = detailData?.package_detail ?? null;
        // P7 F2-F1: the plan v3 names as primary — the same one Plan & Billing shows (older backend: the first-row rule).
        const mainPkg = selectPrimaryPlan(pkgs, detailData?.primary_subscription_id);
        // A Refabric-managed contract (v3 K11) is valid until its term end, not the current period's.
        setPlanEndDate(mainPkg?.managed_until || mainPkg?.end_date || null);
        setPlanName(mainPkg?.name ?? null);
        // REF-5611: only the subscription owner (super admin) may flip the team-wide switch. The
        // authoritative flag is user_info.is_superadmin (DB column `users.is_superadmin`); the
        // top-level is_super_admin is left false by the BE even for owners, so it's only a fallback.
        setIsSuperAdmin(detailData?.user_info?.is_superadmin === true || detailData?.is_super_admin === true);
      } catch (err: any) {
        if (err?.response?.status === 403) {
          setHasAccess(false);
          setRoster(null);
        } else {
          showError(t("account:manage_members.load_error"));
        }
      } finally {
        if (!silent) setIsLoading(false);
      }

      return freshUsage;
    },
    [showError, user.id, t]
  );

  // After a member save: refresh silently (modal stays mounted) and re-point the modal at the
  // fresh member row so its credit numbers + Save-disabled state reflect the save.
  const handleMemberSaved = useCallback(async () => {
    const fresh = await fetchRoster(true);
    setManageMember((prev) => (prev && fresh ? fresh.members.find((m) => m.user_id === prev.user_id) ?? prev : prev));
  }, [fetchRoster]);

  // REF-5611: flip the team-wide "all members share the pool" switch (super-admin only). 403
  // super_admin_required / 400 invalid_policy render inline; anything else is a corner snackbar.
  const handleToggleTeamPolicy = useCallback(
    async (next: boolean) => {
      setSavingPolicy(true);
      setPolicyError(null);

      try {
        const res = await teamsService.setTeamCreditPolicy({ members_unlimited: next });

        if (res?._handled) return;

        AnalyticsEvent("team_credit_policy_saved", "", "success", { members_unlimited: next });
        await fetchRoster(true);
      } catch (err: any) {
        const code = getReasonCode(err);

        // BE exact string not yet confirmed (endpoint not on dev) — match the known variants so
        // routing stays inline regardless; localized copy falls back to the BE message if the
        // catalog key doesn't match.
        if (code === "super_admin_required" || code === "superadmin_required" || code === "teams.superadmin_required")
          setPolicyError(reasonErrorText(err, t("account:manage_members.team_switch.err_super_admin")));
        else if (code === "invalid_policy") setPolicyError(reasonErrorText(err, t("account:manage_members.team_switch.err_invalid_policy")));
        else showError(reasonErrorText(err, t("account:manage_members.team_switch.err_generic")));
      } finally {
        setSavingPolicy(false);
      }
    },
    [fetchRoster, showError, t]
  );

  useEffect(() => {
    fetchRoster();
  }, [fetchRoster]);

  const handleInvite = useCallback(
    async (email: string, role: TeamInviteRole): Promise<boolean> => {
      // Block obvious self-invite locally so we don't waste a round-trip.
      if (user.email && email.toLowerCase() === user.email.toLowerCase()) {
        setInviteEmailError(t("account:manage_members.errors.invite_self"));

        return false;
      }

      // No client-side domain check: the BE no longer restricts invites to the team's email domain
      // (REF-5446) — admins can invite any address, registered or not.
      try {
        const res = await teamsService.invite({ email, role });

        if (res?._handled) return false;

        const data = res.responseData;

        // Seats full → BE held the invite (awaiting_seat, no mail) and asks for a seat purchase first.
        // Close the invite modal and open the seat-change modal at the suggested count; the BE releases
        // the held invite once paid.
        if (data?.action === "seat_purchase_required") {
          setShowInvite(false);
          setSeatUpsell({ suggested: data.suggested_seats ?? (data.seat_used ?? 0) + 1, email });

          return false;
        }

        showSuccess(t("account:manage_members.toasts.invite_sent", { email: data?.email ?? email }));
        await fetchRoster();

        return true;
      } catch (err: any) {
        // Branch on the stable BE reason_code (REF-5446), not message text. Identity/input problems
        // surface inline on the email field; everything else is a corner snackbar.
        const code = getReasonCode(err);

        if (code === "invalid_email" || code === "cannot_invite_self" || code === "already_in_team") {
          setInviteEmailError(reasonErrorText(err, t("account:manage_members.errors.invite_failed")));
        } else if (code === "admin_required") {
          showError(reasonErrorText(err, t("account:manage_members.errors.invite_admin_only")));
        } else {
          // invalid_role, invite_create_failed, invite_email_failed, or an unmapped reason.
          showError(reasonErrorText(err, t("account:manage_members.errors.invite_generic")));
        }

        return false;
      }
    },
    [user.email, fetchRoster, showSuccess, showError, t]
  );

  const handleConfirmRemove = useCallback(async () => {
    if (!removeTarget) return;

    if (removeTarget.kind === "member") {
      const { member } = removeTarget;

      try {
        const res = await teamsService.removeMember(member.id);

        if (res?._handled) return;

        showSuccess(t("account:manage_members.toasts.member_removed", { name: member.name || member.email }));
        await fetchRoster();
      } catch (err: any) {
        const code = getReasonCode(err);

        if (code === "cannot_remove_self") showError(reasonErrorText(err, t("account:manage_members.errors.cannot_remove_self")));
        // user_not_found's catalog text is caller-context ("sign in again") — use a contextual line here.
        else if (code === "user_not_found") showInfo(t("account:manage_members.errors.member_gone"));
        else if (code === "not_team_member" || code === "invalid_member_id") showInfo(reasonErrorText(err, t("account:manage_members.errors.member_gone")));
        else if (code === "admin_required") showError(reasonErrorText(err, t("account:manage_members.errors.remove_admin_only")));
        else showError(reasonErrorText(err, t("account:manage_members.errors.remove_generic")));
      } finally {
        setRemoveTarget(null);
      }
    } else if (removeTarget.kind === "invite") {
      const { invite } = removeTarget;

      try {
        const res = await teamsService.cancelInvite(invite.invite_id);

        if (res?._handled) return;

        showSuccess(t("account:manage_members.toasts.invite_cancelled", { email: invite.email }));
        await fetchRoster();
      } catch (err: any) {
        const code = getReasonCode(err);

        if (code === "admin_required") showError(reasonErrorText(err, t("account:manage_members.errors.cancel_admin_only")));
        else if (code === "invite_not_found" || code === "invalid_invite_id" || code === "invite_not_your_team") showInfo(reasonErrorText(err, t("account:manage_members.errors.invite_gone")));
        else if (code === "invite_not_pending" || code === "invite_already_cancelled" || code === "invite_already_accepted" || code === "invite_expired")
          showInfo(reasonErrorText(err, t("account:manage_members.errors.invite_not_pending")));
        else showError(reasonErrorText(err, t("account:manage_members.errors.cancel_generic")));
      } finally {
        setRemoveTarget(null);
      }
    }
  }, [removeTarget, fetchRoster, showSuccess, showError, showInfo, t]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2.5">
        <div className="p-4 bg-body-100 border border-black-300 rounded-md text-bodyMd text-grey">{t("account:manage_members.loading")}</div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="flex flex-col gap-2.5">
        <div className="flex items-center gap-2.5 p-4 bg-body-100 border border-black-300 rounded-md text-bodyMd text-trueBlack dark:text-white">
          <IconWarning className="w-5 h-5 text-orange flex-shrink-0" />
          {t("account:manage_members.no_access")}
        </div>
      </div>
    );
  }

  if (!roster) {
    return (
      <div className="flex flex-col gap-2.5">
        <div className="p-4 bg-body-100 border border-black-300 rounded-md text-bodyMd text-grey">{t("account:manage_members.no_data")}</div>
      </div>
    );
  }

  const { team, seat_limit, members, pending_invites } = roster;
  // BE seat_used can drift (e.g. removed/historical users left in the counter). Render what
  // the table actually shows so the header agrees with the rows. When truly full, the invite call
  // returns action: "seat_purchase_required" → the seat-upsell modal, so over-limit is still surfaced.
  const visibleSeatsUsed = members.length + pending_invites.length;
  const seatsFull = visibleSeatsUsed >= seat_limit;
  const sortedMembers = adminFirst(members);
  const sortedInvites = adminFirst(pending_invites);

  const query = search.trim().toLowerCase();
  const filteredMembers = query ? sortedMembers.filter((m) => (m.name || "").toLowerCase().includes(query) || m.email.toLowerCase().includes(query)) : sortedMembers;

  // Credit-usage rows keyed by user id, to enrich each roster row + drive the manage-member modal.
  const creditByUserId: Record<number, ITeamCreditUsageMember> = {};
  creditUsage?.members.forEach((cm) => {
    creditByUserId[cm.user_id] = cm;
  });

  // Top stat dashboard — always Plan + Seats; the two credit cards only when credit usage loaded.
  const statCards: React.ReactNode[] = [
    <StatCard key="team" label={t("account:manage_members.stats.team")}>
      <span className="text-h5 text-trueBlack dark:text-white truncate" title={team.name}>
        {team.name}
      </span>
      <span className="text-bodySm text-grey">
        {t("account:manage_members.stats.plan_line", { plan: planName || humanizePackage(team.package_type) })}
        {formatPlanDate(planEndDate) ? t("account:manage_members.stats.active_until_suffix", { date: formatPlanDate(planEndDate) }) : ""}
      </span>
      <Button className="btn-secondary-small w-fit mt-auto" onClick={() => dispatch(setAccountSelectedMenu(AccountSelectMenu.PlanBilling))}>
        {t("account:manage_members.stats.manage_plan")}
      </Button>
    </StatCard>,
    <StatCard key="seats" label={t("account:manage_members.stats.seats")}>
      <span className="text-h4 text-trueBlack dark:text-white">
        {visibleSeatsUsed}
        <span className="text-h6 text-grey"> / {seat_limit}</span>
      </span>
      <ProgressBar maxAmount={seat_limit} currentAmount={visibleSeatsUsed} progressBarClassName="!bg-purple dark:!bg-green" trackClassName="bg-body-200" />
      <span className="text-bodySm text-grey">{t("account:manage_members.stats.seats_available", { available: Math.max(seat_limit - visibleSeatsUsed, 0) })}</span>
      <Button className="btn-secondary-small w-fit mt-auto" onClick={() => setShowSeatChange(true)}>
        {t("account:manage_members.stats.adjust_seats")}
      </Button>
    </StatCard>,
  ];

  if (creditUsage) {
    statCards.push(
      <StatCard
        key="refabric"
        label={
          <>
            {t("account:manage_members.stats.refabric_credits")} <InfoTip tip={t("account:manage_members.credit_tooltips.pool")} />
          </>
        }
      >
        <span className="text-h4 text-trueBlack dark:text-white">
          {creditUsage.refabric_spent.toLocaleString()}
          <span className="text-h6 text-grey"> / {creditUsage.refabric_pool.toLocaleString()}</span>
        </span>
        <ProgressBar maxAmount={creditUsage.refabric_pool} currentAmount={creditUsage.refabric_spent} progressBarClassName="!bg-purple dark:!bg-green" trackClassName="bg-body-200" />
        <span className="flex items-center gap-1 text-bodySm text-grey">
          {t("account:manage_members.stats.unassigned", { amount: creditUsage.unassigned_refabric.toLocaleString() })} <InfoTip tip={t("account:manage_members.credit_tooltips.unassigned")} />
        </span>
        {creditUsage.members_unlimited && <span className="text-bodySm text-purple dark:text-green">{t("account:manage_members.stats.all_share_pool")}</span>}
      </StatCard>,
      <StatCard
        key="model"
        label={
          <>
            {t("account:manage_members.stats.model_credits")} <InfoTip tip={t("account:manage_members.credit_tooltips.model_credits")} />
          </>
        }
      >
        <span className="text-h4 text-trueBlack dark:text-white">
          {creditUsage.model_credits.used.toLocaleString()}
          <span className="text-h6 text-grey"> / {creditUsage.model_credits.total.toLocaleString()}</span>
        </span>
        <ProgressBar maxAmount={creditUsage.model_credits.total} currentAmount={creditUsage.model_credits.used} progressBarClassName="!bg-purple dark:!bg-green" trackClassName="bg-body-200" />
        <span className="text-bodySm text-grey">{t("account:manage_members.stats.team_wide_usage")}</span>
      </StatCard>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Stat dashboard — gap-px over a black-300 surface renders the cell gaps as gridlines. */}
      <div className={clsx("grid grid-cols-1 sm:grid-cols-2 gap-px bg-black-300 rounded-md border border-black-300 overflow-hidden", creditUsage ? "clg:grid-cols-4" : "clg:grid-cols-2")}>
        {statCards}
      </div>

      {/* REF-5611: team-wide no-limit switch — subscription owner (super admin) only, gated on
          user_info.is_superadmin. Sits directly above the members list; title matches the
          "Members" heading (text-h6). Hidden for everyone else, so it degrades safely. */}
      {isSuperAdmin && creditUsage && (
        <div className="flex flex-col gap-2 p-4 bg-body-100 border border-black-300 rounded-md">
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col gap-0.5">
              <span className="flex items-center gap-1 text-h6 text-trueBlack dark:text-white">
                {t("account:manage_members.team_switch.title")} <InfoTip tip={t("account:manage_members.team_switch.tip")} />
              </span>
              <span className="text-bodySm text-grey">{t("account:manage_members.team_switch.desc")}</span>
            </div>
            <ToggleButton
              isOn={!!creditUsage.members_unlimited}
              onToggle={() => {
                if (!savingPolicy) handleToggleTeamPolicy(!creditUsage.members_unlimited);
              }}
              thumbClassName={creditUsage.members_unlimited ? "!bg-purple dark:!bg-green" : "!bg-grey"}
              checkedTrackClassName="!bg-purple/20 dark:!bg-green/20"
            />
          </div>
          {policyError && (
            <div className="flex items-start gap-2 p-2.5 text-bodySm text-orange bg-orange/10 border border-orange/30 rounded-md">
              <IconWarning className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {policyError}
            </div>
          )}
        </div>
      )}

      {/* Members */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <h6 className="text-h6 text-trueBlack dark:text-white">{t("account:manage_members.members_heading", { count: members.length })}</h6>
            <div className="relative">
              <IconSearch className="w-4 h-4 text-grey absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("account:manage_members.search_placeholder")}
                aria-label={t("account:manage_members.search_placeholder")}
                className="w-[220px] xs:w-[150px] bg-body-100 border border-black-300 text-trueBlack dark:text-white text-bodySm rounded-md pl-8 pr-2.5 py-2 outline-none placeholder:text-grey focus:border-grey"
              />
            </div>
          </div>
          <Button className="btn-primary-medium flex items-center gap-1.5" onClick={() => setShowInvite(true)}>
            <IconPlus className="w-4 h-4" />
            {t("account:manage_members.invite_member")}
          </Button>
        </div>

        {showInvite && <InviteModal onInvite={handleInvite} emailError={inviteEmailError} clearEmailError={() => setInviteEmailError(undefined)} onClose={() => setShowInvite(false)} />}

        {seatsFull && (
          <div className="flex items-center gap-2 p-2.5 text-bodySm text-orange bg-orange/10 border border-orange/30 rounded-md">
            <IconWarning className="w-4 h-4 flex-shrink-0" />
            {t("account:manage_members.seats_full_warning")}
          </div>
        )}

        <div className="flex flex-col rounded-md border border-black-300">
          {/* Column header */}
          <div className="flex items-center gap-3 px-3 py-2.5 bg-body-100 rounded-t-md border-b border-black-300 text-bodySm text-grey">
            <span className="flex-1 clg:flex-none clg:w-[340px] min-w-0">{t("account:manage_members.col_member")}</span>
            <span className="w-36 flex-shrink-0">{t("account:manage_members.col_role")}</span>
            <span className="hidden clg:block w-44 flex-shrink-0">{t("account:manage_members.col_usage")}</span>
            <span className="w-16 flex-shrink-0 clg:ml-auto" />
          </div>

          {filteredMembers.length === 0 ? (
            <div className="px-3 py-6 text-center text-bodySm text-grey">{t("account:manage_members.no_members_match", { query: search })}</div>
          ) : (
            filteredMembers.map((member) => {
              const isSelf = !!user.email && member.email.toLowerCase() === user.email.toLowerCase();
              const credit = creditByUserId[member.id];

              const actions: RowAction[] = [];
              if (credit) actions.push({ label: t("account:manage_members.row_manage"), onClick: () => setManageMember(credit) });
              if (!isSelf) actions.push({ label: t("account:manage_members.row_remove"), danger: true, onClick: () => setRemoveTarget({ kind: "member", member }) });

              return (
                <div
                  key={member.id}
                  onClick={credit ? () => setManageMember(credit) : undefined}
                  className={clsx("flex items-center gap-3 px-3 py-2.5 border-b border-black-300 last:border-b-0 hover:bg-body-100/60 transition-colors", credit && "cursor-pointer")}
                >
                  <div className="flex items-center gap-2.5 flex-1 clg:flex-none clg:w-[340px] min-w-0">
                    <Avatar name={member.name} profilePhoto={member.profile_photo} />
                    <div className="flex flex-col min-w-0">
                      <span className="text-bodyMd text-trueBlack dark:text-white truncate">
                        {member.name || member.email}
                        {isSelf && <span className="text-grey text-bodySm ml-1.5">{t("account:manage_members.you")}</span>}
                      </span>
                      <span className="text-bodySm text-grey truncate">{member.email}</span>
                    </div>
                  </div>
                  <div className="w-36 flex-shrink-0">
                    <RoleBadge role={member.role} />
                  </div>
                  <div className="hidden clg:flex w-44 flex-shrink-0 flex-col gap-1">
                    {credit ? (
                      credit.is_unlimited ? (
                        <>
                          <NoLimitBadge />
                          <span className="text-bodySm text-grey">{t("account:manage_members.used", { amount: credit.spent.toLocaleString() })}</span>
                        </>
                      ) : (
                        <>
                          <span className="text-bodySm text-grey">
                            {credit.spent.toLocaleString()}/{credit.cap.toLocaleString()}
                          </span>
                          <ProgressBar maxAmount={credit.cap} currentAmount={credit.spent} progressBarClassName="!bg-purple dark:!bg-green" trackClassName="bg-body-200" />
                        </>
                      )
                    ) : (
                      <span className="text-bodySm text-grey">—</span>
                    )}
                  </div>
                  <div className="w-16 flex-shrink-0 clg:ml-auto flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    <RowActionsMenu actions={actions} label={t("account:manage_members.row_actions", { name: member.name || member.email })} />
                    {credit && (
                      <button
                        type="button"
                        aria-label={t("account:manage_members.row_open", { name: member.name || member.email })}
                        className="flex-center w-7 h-7 rounded-md text-grey hover:text-trueBlack dark:hover:text-white hover:bg-black-300/60"
                        onClick={() => setManageMember(credit)}
                      >
                        <IconChevron className="w-4 h-4 rotate-90" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}

          {/* Team total */}
          {creditUsage && filteredMembers.length > 0 && (
            <div className="flex items-center gap-3 px-3 py-2.5 border-t border-black-300 bg-body-100 rounded-b-md text-bodySm">
              <span className="flex-1 clg:flex-none clg:w-[340px] text-grey">{t("account:manage_members.team_total")}</span>
              <span className="w-36 flex-shrink-0" />
              <span className="hidden clg:block w-44 flex-shrink-0 text-trueBlack dark:text-white">
                {creditUsage.refabric_spent.toLocaleString()}/{creditUsage.refabric_pool.toLocaleString()}
              </span>
              <span className="w-16 flex-shrink-0 clg:ml-auto" />
            </div>
          )}
        </div>
      </div>

      {/* Pending invites */}
      {pending_invites.length > 0 && (
        <div className="flex flex-col gap-3">
          <h6 className="text-h6 text-trueBlack dark:text-white">{t("account:manage_members.pending_heading", { count: pending_invites.length })}</h6>
          <div className="flex flex-col rounded-md border border-black-300">
            {sortedInvites.map((invite) => {
              const isExpired = invite.status === "expired";
              const expiresLabel = formatExpiresAt(invite.expires_at);
              const actions: RowAction[] = [{ label: t("account:manage_members.cancel_invite"), danger: true, onClick: () => setRemoveTarget({ kind: "invite", invite }) }];

              return (
                <div key={invite.invite_id} className="flex items-center gap-3 px-3 py-2.5 border-b border-black-300 last:border-b-0 hover:bg-body-100/60 transition-colors">
                  <div className="flex items-center gap-2.5 flex-1 clg:flex-none clg:w-[340px] min-w-0">
                    <div className="flex-center rounded-full bg-black-300 w-9 h-9 text-grey flex-shrink-0">
                      <IconUser className="w-4 h-4" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-bodyMd text-trueBlack dark:text-white truncate">{invite.email}</span>
                      <span className="text-bodySm text-grey truncate">
                        {t("account:manage_members.invited_by", { name: invite.invited_by })}
                        {expiresLabel && t("account:manage_members.expires_suffix", { date: expiresLabel })}
                      </span>
                    </div>
                  </div>
                  <div className="w-36 flex-shrink-0">
                    <RoleBadge role={invite.role} />
                  </div>
                  <div className="hidden clg:flex w-44 flex-shrink-0">
                    <span className={clsx("text-bodySm px-2 py-0.5 rounded", isExpired ? "bg-orange/20 text-orange" : "bg-black-300 text-grey")}>
                      {isExpired ? t("account:manage_members.expired") : t("account:manage_members.pending")}
                    </span>
                  </div>
                  <div className="w-16 flex-shrink-0 clg:ml-auto flex justify-end">
                    <RowActionsMenu actions={actions} label={t("account:manage_members.invite_actions", { email: invite.email })} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <DeleteModal
        show={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        onDelete={handleConfirmRemove}
        title={removeTarget?.kind === "invite" ? t("account:manage_members.remove_modal.cancel_invite_title") : t("account:manage_members.remove_modal.remove_member_title")}
        message={
          removeTarget?.kind === "invite"
            ? t("account:manage_members.remove_modal.cancel_invite_message", { email: removeTarget.invite.email })
            : removeTarget?.kind === "member"
            ? t("account:manage_members.remove_modal.remove_member_message", { name: removeTarget.member.name || removeTarget.member.email })
            : ""
        }
        deleteButtonText={removeTarget?.kind === "invite" ? t("account:manage_members.remove_modal.cancel_invite_button") : t("account:manage_members.remove_modal.remove_button")}
        cancelButtonText={t("account:manage_members.remove_modal.keep")}
      />

      {seatUpsell && (
        <SeatChangeModal
          onClose={() => setSeatUpsell(null)}
          currentSeats={seat_limit}
          seatUsed={visibleSeatsUsed}
          initialTarget={seatUpsell.suggested}
          intro={t("account:manage_members.seat_change.upsell_intro", { email: seatUpsell.email })}
        />
      )}

      {showSeatChange && <SeatChangeModal onClose={() => setShowSeatChange(false)} currentSeats={seat_limit} seatUsed={visibleSeatsUsed} />}

      {manageMember && creditUsage && (
        <ManageMemberModal
          member={manageMember}
          pool={creditUsage.refabric_pool}
          unassigned={creditUsage.unassigned_refabric}
          membersUnlimited={!!creditUsage.members_unlimited}
          planActiveUntil={formatPlanDate(planEndDate)}
          profilePhoto={members.find((m) => m.id === manageMember.user_id)?.profile_photo ?? null}
          onRemove={
            user.email && manageMember.email.toLowerCase() === user.email.toLowerCase()
              ? undefined
              : () => {
                  const rosterMember = members.find((m) => m.id === manageMember.user_id);
                  if (rosterMember) setRemoveTarget({ kind: "member", member: rosterMember });
                }
          }
          onClose={() => setManageMember(null)}
          onSaved={handleMemberSaved}
        />
      )}
    </div>
  );
};

export default ManageMembers;
